// src/api/loaRequests.ts
//
// Admin "LOA requests" page (#687): the campaign asking clients who have not
// signed an LOA to sign one, a few each day.
// Backend: python-backend/app/routes/admin_loa_request_backlog.py
//          python-backend/app/services/loa_request_daily_runner.py

import { client } from './claims';

const BASE = '/ui/admin/loa-request-backlog';

export interface LoaCampaignSettings {
  updatedLoa: 'any_signed' | 'august_wording';
  includeAskedBefore: boolean;
  exclude70Plus: boolean;
  includeSiblingCancelled: boolean;
}

export interface LoaSkippedReason {
  reason: string;
  count: number;
}

export interface LoaCampaignStatus {
  campaign: {
    ready: boolean;
    problems: string[];
    notChosen: string[];
    settings: LoaCampaignSettings | null;
    plain: string[];
  };
  sendEnabled: boolean;
  runner: {
    enabled: boolean;
    intervalMinutes: number;
    hours: string | null;
    hoursOk: boolean;
    withinHoursNow: boolean;
  };
  dailyCap: number;
  sentToday: number | null;
  totalSent: number | null;
  leftToCheck: number | null;
  cursor: string;
  sweeps: number;
  lastRun: {
    at: string | null;
    outcome: string | null;
    checked: number;
    sent: number;
    skipped: LoaSkippedReason[];
  };
  halted: { at: string; reason: string } | null;
  /** Client records skipped because another record with the same email was already asked (merge them). */
  duplicates?: {
    count: number;
    items: Array<{
      clientId: string;
      askedClientId: string | null;
      claimRefs: string[];
      emailDomain: string | null;
      lastSeenAt: string | null;
    }>;
  } | null;
  limits: { checkSize: number; testBatchSize: number };
}

export type LoaRowOutcome = 'would-send' | 'sent' | 'skipped' | 'not-sent' | 'failed' | 'error' | 'not-checked';

export interface LoaBacklogRow {
  clientId: string;
  outcome: LoaRowOutcome;
  group?: string | null;
  claimRefs?: string[];
  linkClaimRefs?: string[];
  skipCode?: string;
  skipReason?: string;
  detail?: string;
}

export interface LoaBacklogResult {
  dryRun: boolean;
  results: LoaBacklogRow[];
  nextAfter: string;
  complete: boolean;
  stopped: string | null;
  dailyCap: number;
  sentToday: number | null;
  suppressionsChecked?: boolean;
}

export const getLoaCampaign = () =>
  client.get<LoaCampaignStatus>(`${BASE}/campaign`, { timeout: 60000 }).then((res) => res.data);

const campaignBody = (settings: LoaCampaignSettings) => ({
  updatedLoa: settings.updatedLoa,
  includeAskedBefore: settings.includeAskedBefore,
  exclude70Plus: settings.exclude70Plus,
  includeSiblingCancelled: settings.includeSiblingCancelled,
});

/** A dry run of the next clients from where the daily sending has got to: nothing is sent. */
export const checkLoaRequests = (settings: LoaCampaignSettings, after: string, maxChecks: number) =>
  client
    .post<LoaBacklogResult>(
      BASE,
      { ...campaignBody(settings), dryRun: true, after, maxChecks },
      { timeout: 240000 },
    )
    .then((res) => res.data);

/** Really sends: at most ``maxSends`` LOA request emails, a second apart. */
export const sendLoaTestBatch = (settings: LoaCampaignSettings, after: string, maxSends: number) =>
  client
    .post<LoaBacklogResult>(
      BASE,
      { ...campaignBody(settings), dryRun: false, after, maxSends, confirmMaxSends: maxSends },
      { timeout: 300000 },
    )
    .then((res) => res.data);

export const resumeLoaRunner = () =>
  client.post<{ resumed: boolean }>(`${BASE}/runner/resume`).then((res) => res.data);
